/* eslint-disable react/no-array-index-key, consistent-return, @typescript-eslint/no-shadow, prettier/prettier, @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any */
import React from 'react';
import { useStyletron } from 'baseui';
import { StyledTable, StyledHeadCell, StyledBodyCell } from 'baseui/table-grid';

interface SegmentImpactMatrixProps {
	impacts: any[];
}

const getChangeType = (current: string, proposed: string): string => {
	const c = (current || '').toLowerCase();
	const p = (proposed || '').toLowerCase();
	if (!c && !p) return 'modified';
	if (c === p) return 'no-change';
	if (c.includes('disabled') && p.includes('enabled')) return 'enabled';
	if (c.includes('enabled') && p.includes('disabled')) return 'disabled';
	return 'modified';
};

const CHANGE_COLORS: Record<string, { bg: string; text: string; label: string }> = {
	'no-change': { bg: '#F0F0F0', text: '#666', label: 'No Change' },
	modified: { bg: '#FFF8E1', text: '#996300', label: 'Modified' },
	enabled: { bg: '#E6F7ED', text: '#05944F', label: 'Enabled' },
	disabled: { bg: '#FFF3F0', text: '#E11900', label: 'Disabled' }
};

const SegmentImpactMatrix: React.FC<SegmentImpactMatrixProps> = ({ impacts }) => {
	const [css] = useStyletron();

	if (!impacts?.length) {
		return <div className={css({ padding: '16px', color: '#666' })}>No segment-specific impacts identified.</div>;
	}

	const hasSplitBehaviors = impacts.some((i) => i?.currentBehavior !== undefined || i?.newBehavior !== undefined);

	if (hasSplitBehaviors) {
		return (
			<StyledTable $gridTemplateColumns='1.2fr 2fr 2fr 1fr'>
				<StyledHeadCell>Segment</StyledHeadCell>
				<StyledHeadCell>Current Behavior</StyledHeadCell>
				<StyledHeadCell>New Behavior</StyledHeadCell>
				<StyledHeadCell>Change Type</StyledHeadCell>
				{impacts.map((impact, idx) => {
					const current = impact?.currentBehavior || '';
					const proposed = impact?.newBehavior || '';
					const changeType = getChangeType(current, proposed);
					const colors = CHANGE_COLORS[changeType];
					return (
						<React.Fragment key={idx}>
							<StyledBodyCell>
								<strong>{impact?.segment || '(unknown)'}</strong>
							</StyledBodyCell>
							<StyledBodyCell>
								<span className={css({ fontSize: '13px' })}>{current}</span>
							</StyledBodyCell>
							<StyledBodyCell>
								<span className={css({ fontSize: '13px' })}>{proposed}</span>
							</StyledBodyCell>
							<StyledBodyCell>
								<span
									className={css({
										backgroundColor: colors.bg,
										color: colors.text,
										padding: '4px 10px',
										borderRadius: '12px',
										fontSize: '12px',
										fontWeight: 600
									})}>
									{colors.label}
								</span>
							</StyledBodyCell>
						</React.Fragment>
					);
				})}
			</StyledTable>
		);
	}

	return (
		<StyledTable $gridTemplateColumns='1.2fr 3fr'>
			<StyledHeadCell>Segment</StyledHeadCell>
			<StyledHeadCell>Impact</StyledHeadCell>
			{impacts.map((impact, idx) => (
				<React.Fragment key={idx}>
					<StyledBodyCell>
						<strong>{impact?.segment || '(unknown)'}</strong>
					</StyledBodyCell>
					<StyledBodyCell>
						<span className={css({ fontSize: '13px' })}>
							{impact?.impact || impact?.description || ''}
						</span>
					</StyledBodyCell>
				</React.Fragment>
			))}
		</StyledTable>
	);
};

export default SegmentImpactMatrix;
